import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '../collections';
import { UserProfile } from '@/lib/auth/types';
import { handleError } from '@/lib/utils/errors';
import { SUBSCRIPTION_PLANS } from './subscriptionService';

export async function createUserProfile(user: User): Promise<UserProfile> {
  if (!user.email) {
    throw new Error('User email is required');
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || undefined,
        createdAt: Timestamp.now(),
        lastLoginAt: Timestamp.now(),
        subscription: {
          tier: 'FREE',
          startDate: Timestamp.now(),
          autoRenew: false,
          analysisCount: 0,
          lastResetDate: Timestamp.now(),
          features: SUBSCRIPTION_PLANS.FREE.features
        }
      };

      await setDoc(userRef, userProfile);
      return userProfile;
    }

    const existingProfile = userSnap.data() as UserProfile;
    
    // Add subscription if it doesn't exist
    if (!existingProfile.subscription) {
      const updatedProfile = {
        ...existingProfile,
        lastLoginAt: Timestamp.now(),
        subscription: {
          tier: 'FREE',
          startDate: Timestamp.now(),
          autoRenew: false,
          analysisCount: 0,
          lastResetDate: Timestamp.now(),
          features: SUBSCRIPTION_PLANS.FREE.features
        }
      };

      await setDoc(userRef, updatedProfile);
      return updatedProfile;
    }

    await updateUserLastLogin(user.uid);
    return {
      ...existingProfile,
      lastLoginAt: Timestamp.now()
    };
  } catch (error) {
    throw handleError(error);
  }
}

export async function updateUserLastLogin(uid: string): Promise<void> {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    await setDoc(userRef, {
      lastLoginAt: Timestamp.now()
    }, { merge: true });
  } catch (error) {
    throw handleError(error);
  }
}